import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { PageHeader } from '../../../components/ui/PageHeader'
import { Spinner } from '../../../components/ui/Spinner'
import { Modal } from '../../../components/ui/Modal'
import { Pagination } from '../../../components/ui/Pagination'
import { Alert } from '../../../components/ui/Alert'
import { ConfirmDialog } from '../../../components/ui/ConfirmDialog'
import { FormField, inputClass, selectClass } from '../../../components/ui/FormField'
import { useEtapas, useCreateEtapa, useUpdateEtapa, useDeleteEtapa } from '../hooks/useEtapas'
import { useCampeonatos } from '../../campeonatos/hooks/useCampeonatos'
import { useModalidades } from '../../modalidades/hooks/useModalidades'
import { etapaFormSchema, type EtapaFormData } from '../schemas'
import type { Etapa } from '../types'

function formatDate(dt: string) {
  if (!dt) return '—'
  return new Date(dt).toLocaleDateString('pt-BR')
}

export function EtapasPage() {
  const [searchParams] = useSearchParams()
  const preloadCampeonato = searchParams.get('idcampeonato') ? parseInt(searchParams.get('idcampeonato')!, 10) : undefined
  const preloadModalidade = searchParams.get('idmodalidade') ? parseInt(searchParams.get('idmodalidade')!, 10) : undefined

  const [page, setPage] = useState(1)
  const [filterCampeonato, setFilterCampeonato] = useState(preloadCampeonato)
  const [filterModalidade, setFilterModalidade] = useState(preloadModalidade)
  const [filterTemporada, setFilterTemporada] = useState<number | undefined>(undefined)
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<Etapa | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<Etapa | null>(null)
  const [alert, setAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null)
  const [selectedCampeonato, setSelectedCampeonato] = useState(preloadCampeonato || 0)

  const { data, isLoading } = useEtapas({
    idcampeonato: filterCampeonato, idmodalidade: filterModalidade, temporada: filterTemporada, page, limit: 20,
  })
  const { data: campeonatosData } = useCampeonatos({ limit: 200 })
  const { data: modalidadesData } = useModalidades({ idcampeonato: selectedCampeonato || undefined, limit: 200 })
  const createEtapa = useCreateEtapa()
  const updateEtapa = useUpdateEtapa()
  const deleteEtapa = useDeleteEtapa()

  const form = useForm<EtapaFormData>({
    resolver: zodResolver(etapaFormSchema),
    defaultValues: {
      idcampeonato: preloadCampeonato || 0,
      idmodalidade: preloadModalidade || 0,
      titulo: '', titulo_reduzido: '',
      temporada: new Date().getFullYear(), numero_etapa: 1,
      data_inicio: '', data_encerramento: '', data_encerramento_inscricoes: '',
    },
  })

  function openCreate() {
    setEditing(null)
    form.reset({
      idcampeonato: filterCampeonato || 0, idmodalidade: filterModalidade || 0,
      titulo: '', titulo_reduzido: '', temporada: new Date().getFullYear(), numero_etapa: 1,
      data_inicio: '', data_encerramento: '', data_encerramento_inscricoes: '',
    })
    setSelectedCampeonato(filterCampeonato || 0)
    setModalOpen(true)
  }

  function openEdit(e: Etapa) {
    setEditing(e)
    setSelectedCampeonato(Number(e.idcampeonato))
    form.reset({
      idcampeonato: Number(e.idcampeonato), idmodalidade: Number(e.idmodalidade),
      titulo: e.titulo, titulo_reduzido: e.titulo_reduzido,
      temporada: Number(e.temporada), numero_etapa: Number(e.numero_etapa),
      data_inicio: e.data_inicio?.split('T')[0] ?? '',
      data_encerramento: e.data_encerramento?.split('T')[0] ?? '',
      data_encerramento_inscricoes: e.data_encerramento_inscricoes?.split('T')[0] ?? '',
    })
    setModalOpen(true)
  }

  async function handleSubmit(formData: EtapaFormData) {
    try {
      if (editing) {
        await updateEtapa.mutateAsync({ id: editing.idetapa, data: formData })
        setAlert({ type: 'success', message: 'Etapa atualizada com sucesso' })
      } else {
        await createEtapa.mutateAsync(formData as any)
        setAlert({ type: 'success', message: 'Etapa cadastrada com sucesso' })
      }
      setModalOpen(false)
    } catch (err: any) {
      setAlert({ type: 'error', message: err.response?.data?.error ?? 'Erro ao salvar etapa' })
    }
  }

  async function handleDelete() {
    if (!deleteTarget) return
    try {
      await deleteEtapa.mutateAsync(deleteTarget.idetapa)
      setAlert({ type: 'success', message: 'Etapa excluida com sucesso' })
    } catch (err: any) {
      setAlert({ type: 'error', message: err.response?.data?.error ?? 'Erro ao excluir etapa' })
    } finally {
      setDeleteTarget(null)
    }
  }

  const meta = data?.meta
  const isSaving = createEtapa.isPending || updateEtapa.isPending

  return (
    <div className="space-y-4">
      <PageHeader
        title="Etapas"
        description="Gerenciar etapas dos campeonatos"
        action={
          <button onClick={openCreate} className="px-4 py-2 bg-compumaq-dourado text-compumaq-texto text-sm rounded-lg hover:bg-compumaq-doradoClaro">
            + Nova Etapa
          </button>
        }
      />

      {alert && <Alert type={alert.type} message={alert.message} onClose={() => setAlert(null)} />}

      <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 p-4 shadow-sm">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Campeonato</label>
            <select className={selectClass} value={filterCampeonato || ''}
              onChange={e => { const v = parseInt(e.target.value, 10) || undefined; setFilterCampeonato(v); setFilterModalidade(undefined); setPage(1) }}>
              <option value="">Todos</option>
              {campeonatosData?.data?.map(c => <option key={c.idcampeonato} value={c.idcampeonato}>{c.descricao} ({c.temporada})</option>)}
            </select>
          </div>
          <div className="flex-1 min-w-[180px]">
            <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Modalidade</label>
            <select className={selectClass} value={filterModalidade || ''}
              onChange={e => { setFilterModalidade(parseInt(e.target.value, 10) || undefined); setPage(1) }}>
              <option value="">Todas</option>
              {filterCampeonato && modalidadesData?.data?.map(m => <option key={m.idmodalidade} value={m.idmodalidade}>{m.nome} ({m.grupo})</option>)}
            </select>
          </div>
          <div className="w-24">
            <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Temporada</label>
            <input className={inputClass} type="number" placeholder="2025" value={filterTemporada || ''}
              onChange={e => setFilterTemporada(parseInt(e.target.value, 10) || undefined)} />
          </div>
        </div>
      </div>

      <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center py-16"><Spinner size="lg" /></div>
        ) : !data?.data?.length ? (
          <div className="text-center py-16 text-compumaq-textoSuave">
            <p className="text-4xl mb-3">📅</p>
            <p className="font-medium">Nenhuma etapa encontrada</p>
          </div>
        ) : (
          <>
            <table className="w-full text-sm">
              <thead className="bg-compumaq-preto border-b border-compumaq-dourado/25">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-compumaq-textoSuave">Titulo</th>
                  <th className="text-left px-4 py-3 font-medium text-compumaq-textoSuave">Campeonato</th>
                  <th className="text-left px-4 py-3 font-medium text-compumaq-textoSuave">Modalidade</th>
                  <th className="text-left px-4 py-3 font-medium text-compumaq-textoSuave">N. Etapa</th>
                  <th className="text-left px-4 py-3 font-medium text-compumaq-textoSuave">Temporada</th>
                  <th className="text-left px-4 py-3 font-medium text-compumaq-textoSuave">Inicio</th>
                  <th className="text-left px-4 py-3 font-medium text-compumaq-textoSuave">Encerramento</th>
                  <th className="text-right px-4 py-3 font-medium text-compumaq-textoSuave">Acoes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-compumaq-dourado/25">
                {data.data.map((e) => (
                  <tr key={e.idetapa} className="hover:bg-compumaq-preto">
                    <td className="px-4 py-3 font-medium text-compumaq-texto">{e.titulo_reduzido}</td>
                    <td className="px-4 py-3 text-compumaq-textoSuave text-xs">{e.campeonato_descricao}</td>
                    <td className="px-4 py-3 text-compumaq-textoSuave text-xs">{e.modalidade_nome}</td>
                    <td className="px-4 py-3 text-center">
                      <span className="inline-block bg-compumaq-pretoSuave text-compumaq-texto text-xs font-medium px-2 py-0.5 rounded">
                        {e.numero_etapa}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-compumaq-textoSuave">{e.temporada}</td>
                    <td className="px-4 py-3 text-compumaq-textoSuave">{formatDate(e.data_inicio)}</td>
                    <td className="px-4 py-3 text-compumaq-textoSuave">{formatDate(e.data_encerramento)}</td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        <button onClick={() => openEdit(e)} className="text-xs text-compumaq-doradoClaro hover:text-compumaq-doradoClaro font-medium">Editar</button>
                        <button onClick={() => setDeleteTarget(e)} className="text-xs text-red-600 hover:text-red-800 font-medium">Excluir</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {meta && meta.totalPages > 1 && (
              <Pagination page={page} totalPages={meta.totalPages} total={meta.total} limit={20} onPageChange={setPage} />
            )}
          </>
        )}
      </div>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editing ? 'Editar Etapa' : 'Nova Etapa'} size="xl">
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Campeonato" required error={form.formState.errors.idcampeonato?.message}>
              <Controller control={form.control} name="idcampeonato" render={({ field }) => (
                <select className={selectClass} value={field.value || ''} onChange={e => {
                  const v = parseInt(e.target.value, 10) || 0
                  field.onChange(v)
                  setSelectedCampeonato(v)
                  form.setValue('idmodalidade', 0)
                }}>
                  <option value="">Selecione...</option>
                  {campeonatosData?.data?.map(c => (
                    <option key={c.idcampeonato} value={c.idcampeonato}>{c.descricao} ({c.temporada})</option>
                  ))}
                </select>
              )} />
            </FormField>
            <FormField label="Modalidade" required error={form.formState.errors.idmodalidade?.message}>
              <Controller control={form.control} name="idmodalidade" render={({ field }) => (
                <select className={selectClass} value={field.value || ''} onChange={e => field.onChange(parseInt(e.target.value, 10) || 0)}>
                  <option value="">Selecione...</option>
                  {modalidadesData?.data?.map(m => (
                    <option key={m.idmodalidade} value={m.idmodalidade}>{m.nome} ({m.grupo})</option>
                  ))}
                </select>
              )} />
            </FormField>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Titulo" required error={form.formState.errors.titulo?.message}>
              <input className={inputClass} {...form.register('titulo')} placeholder="Titulo completo da etapa" />
            </FormField>
            <FormField label="Titulo reduzido" required error={form.formState.errors.titulo_reduzido?.message}>
              <input className={inputClass} {...form.register('titulo_reduzido')} placeholder="Titulo curto" />
            </FormField>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Temporada" required error={form.formState.errors.temporada?.message}>
              <input className={inputClass} type="number" {...form.register('temporada', { valueAsNumber: true })} />
            </FormField>
            <FormField label="Numero da Etapa (1-6)" required error={form.formState.errors.numero_etapa?.message}>
              <Controller control={form.control} name="numero_etapa" render={({ field }) => (
                <select className={selectClass} value={field.value} onChange={e => field.onChange(parseInt(e.target.value, 10))}>
                  {[1, 2, 3, 4, 5, 6].map(n => <option key={n} value={n}>Etapa {n}</option>)}
                </select>
              )} />
            </FormField>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <FormField label="Data Inicio" required error={form.formState.errors.data_inicio?.message}>
              <input className={inputClass} type="date" {...form.register('data_inicio')} />
            </FormField>
            <FormField label="Encerramento" required error={form.formState.errors.data_encerramento?.message}>
              <input className={inputClass} type="date" {...form.register('data_encerramento')} />
            </FormField>
            <FormField label="Enc. Inscricoes" required error={form.formState.errors.data_encerramento_inscricoes?.message}>
              <input className={inputClass} type="date" {...form.register('data_encerramento_inscricoes')} />
            </FormField>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={() => setModalOpen(false)}
              className="px-4 py-2 border border-compumaq-dourado/25 text-sm rounded-lg hover:bg-compumaq-preto">Cancelar</button>
            <button type="submit" disabled={isSaving}
              className="px-4 py-2 bg-compumaq-dourado text-compumaq-texto text-sm rounded-lg hover:bg-compumaq-doradoClaro disabled:opacity-50">
              {isSaving ? 'Salvando...' : editing ? 'Atualizar' : 'Cadastrar'}
            </button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Excluir Etapa"
        message={`Excluir "${deleteTarget?.titulo}"?`}
        loading={deleteEtapa.isPending}
      />
    </div>
  )
}
